import type { PreparedCall, TradeIntent, TradeQuote } from '@hoodmorning/contracts';
import { decodeAbiParameters, decodeFunctionData, encodeAbiParameters, encodeFunctionData, getAddress, isAddressEqual, parseAbi, parseAbiParameters, zeroAddress, type Address } from 'viem';
import { hashPreparedCalls } from './calls.js';
import { inspectRouterExecution, routerExecuteAbi } from './routerInspection.js';
import { inspectRouterExecutionV212 } from './routerInspectionV212.js';
import { terminalRouterVersionForDecoder } from './terminalRouterVersion.js';
import { validatePreparedTrade, type TradePolicy, type VerifiedTradeDecoding } from './tradePolicy.js';

const approvalAbi = parseAbi(['function approve(address spender,uint256 amount) returns (bool)']);
const permit2ApprovalAbi = parseAbi(['function approve(address token,address spender,uint160 amount,uint48 expiration)']);
const swapParams = parseAbiParameters('address recipient,uint256 amountIn,uint256 amountOutMin,address[] path,bool payerIsUser,uint256[] minHopPriceX36');
const v3SwapParams = parseAbiParameters('address recipient,uint256 amountIn,uint256 amountOutMin,bytes path,bool payerIsUser,uint256[] minHopPriceX36');
const paymentParams = parseAbiParameters('address token,address recipient,uint256 amount');
const routerRecipient = '0x0000000000000000000000000000000000000002';
const senderRecipient = '0x0000000000000000000000000000000000000001';

/** Actual canonical command path, not provider route metadata or pool authenticity proof. */
export type ActualRouterTradeDecoding = VerifiedTradeDecoding & {
  route: { family: 'V2'; tokens: Address[] } | { family: 'V3'; tokens: Address[]; fees: number[] };
};

/** Decode actual bytes, never a provider-supplied "verified" object.
 * Currently covers ERC20 V2 exact-input -> output fee -> net sweep only.
 * Other approved route families still need validators; unsupported is not verified.
 * This proves calldata policy, NOT pool authenticity, allowance readiness, simulation,
 * chain/runtime pins, token behavior, or authorization to submit a transaction.
 */
export function decodeAndValidateV2Trade(input: {
  intent: TradeIntent<4663 | 46630>; quote: TradeQuote; calls: readonly PreparedCall[]; policy: TradePolicy;
}): ActualRouterTradeDecoding {
  return decodeTrade(input, 'V2');
}

/** ERC20 V3 exact-input only, with PAY_PORTION or exact-bps FULL_PRECISION then
 * SWEEP. Source: Uniswap/universal-router 2.1.1 commit
 * 999d561c3ad58fb5cab91b602911f3c75591a9c7 Dispatcher/V3SwapRouter/Payments.
 * Reviewed SDK/router pairs 5.11.3/2.1.1 and 5.12.0/2.1.2 encode six swap
 * fields, including optional hop prices. Only the captured policy pin selects
 * the reader; compatible bytes and quote metadata cannot select or upgrade it.
 * Structural policy proof only: pool/factory/runtime, Permit2 allowance, actual
 * consumption/output, token semantics and simulation remain external gates.
 */
export function decodeAndValidateV3Trade(input: {
  intent: TradeIntent<4663 | 46630>; quote: TradeQuote; calls: readonly PreparedCall[]; policy: TradePolicy;
}): ActualRouterTradeDecoding {
  return decodeTrade(input, 'V3');
}

function decodeTrade(input: Parameters<typeof decodeAndValidateV2Trade>[0], family: 'V2' | 'V3'): ActualRouterTradeDecoding {
  const { intent, quote, policy } = input;
  const routerVersion = terminalRouterVersionForDecoder(policy.requiredDecoderVersion);
  const calls = input.calls.map(call => ({ ...call }));
  if (intent.tokenIn === 'NATIVE' || intent.tokenOut === 'NATIVE') throw new Error('Native route validator not implemented');
  const tokenIn = getAddress(intent.tokenIn);
  const tokenOut = getAddress(intent.tokenOut);
  if (calls.length !== 1 && calls.length !== 2 && calls.length !== 4) throw new Error('Unsupported call coverage');
  const callIndex = calls.length - 1;
  const call = calls[callIndex]!;
  if (!isAddressEqual(call.to, policy.router) || call.value !== 0n) throw new Error('Invalid router call');
  const inspected = routerVersion === '2.1.1'
    ? inspectRouterExecution(call.data, policy.nowUnixSeconds)
    : inspectRouterExecutionV212(call.data, policy.nowUnixSeconds);
  const { args: [commands, inputs] } = decodeFunctionData({ abi: routerExecuteAbi, data: call.data });
  const flow = commands.toLowerCase();
  if (family === 'V2' ? flow !== '0x080604' : flow !== '0x000604' && flow !== '0x000704') throw new Error('Unsupported command flow');
  const poolFees: number[] = [];
  const swap = family === 'V2' ? decodeAbiParameters(swapParams, inputs[0]!) : (() => {
    const decoded = decodeAbiParameters(v3SwapParams, inputs[0]!);
    if (encodeAbiParameters(v3SwapParams, decoded).toLowerCase() !== inputs[0]!.toLowerCase()) throw new Error('Noncanonical command input');
    const [recipient, amountIn, minimum, packedPath, payer, prices] = decoded;
    const bytes = (packedPath.length - 2) / 2;
    if (bytes < 43 || (bytes - 20) % 23 !== 0 || (bytes - 20) / 23 > 7) throw new Error('Invalid V3 path length');
    const path = [getAddress(packedPath.slice(0, 42))];
    for (let offset = 42; offset < packedPath.length; offset += 46) {
      const poolFee = Number.parseInt(packedPath.slice(offset, offset + 6), 16);
      if (poolFee >= 1_000_000) throw new Error('Invalid V3 pool fee');
      poolFees.push(poolFee);
      path.push(getAddress(`0x${packedPath.slice(offset + 6, offset + 46)}`));
    }
    if (prices.length !== 0 && prices.length !== path.length - 1) throw new Error('V3 hop price count mismatch');
    if (amountIn === 0n || amountIn > (1n << 160n) - 1n) throw new Error('Unsupported V3 input amount');
    return [recipient, amountIn, minimum, path, payer, prices] as const;
  })();
  const fee = decodeAbiParameters(paymentParams, inputs[1]!);
  const sweep = decodeAbiParameters(paymentParams, inputs[2]!);
  // Decode/re-encode each payload too: outer ABI canonicality alone does not
  // reject hidden trailing bytes inside a command's bytes payload.
  if ((family === 'V2' && encodeAbiParameters(swapParams, swap).toLowerCase() !== inputs[0]!.toLowerCase())
    || encodeAbiParameters(paymentParams, fee).toLowerCase() !== inputs[1]!.toLowerCase()
    || encodeAbiParameters(paymentParams, sweep).toLowerCase() !== inputs[2]!.toLowerCase()) throw new Error('Noncanonical command input');
  const [recipient, amountIn, grossMinimum, path, payerIsUser, hopPrices] = swap;
  if (!isAddressEqual(recipient, routerRecipient) || !payerIsUser) throw new Error('Swap must pay from vault and retain output for fee settlement');
  if (path.length < 2 || path.length > 8 || (family === 'V2' && hopPrices.length !== path.length - 1)
    || path.some(token => isAddressEqual(token, zeroAddress))
    || new Set(path.map(token => token.toLowerCase())).size !== path.length) throw new Error(`Invalid ${family} path`);
  if (!isAddressEqual(path[0]!, tokenIn) || !isAddressEqual(path.at(-1)!, tokenOut)) throw new Error('Swap asset mismatch');
  if (!isAddressEqual(fee[0], tokenOut) || !isAddressEqual(sweep[0], tokenOut)) throw new Error('Payment asset mismatch');
  const feeScale = flow === '0x000704' ? 100_000_000_000_000n : 1n;
  if (fee[2] !== BigInt(policy.requiredFeeBps) * feeScale) throw new Error('Fee rate mismatch');
  if (sweep[2] > (1n << 160n) - 1n) throw new Error('Sweep minimum exceeds deployed uint160 width');
  const recipientIsSender = isAddressEqual(sweep[1], senderRecipient);
  if (!recipientIsSender && !isAddressEqual(sweep[1], policy.vault)) throw new Error('Sweep recipient is not vault');
  // Require the swap itself to protect net output, rather than depending on
  // unrelated pre-existing router balances to satisfy the final sweep minimum.
  if (grossMinimum - grossMinimum * fee[2] / (10_000n * feeScale) < BigInt(quote.minimumAmountOut)) throw new Error('Net swap minimum too low');
  const decoded: ActualRouterTradeDecoding = {
    verified: true, decoder: inspected.decoder, decoderVersion: inspected.decoderVersion,
    routerVersion: inspected.routerVersion, callsHash: hashPreparedCalls(calls), commands: [],
    route: family === 'V2' ? { family, tokens: [...path] } : { family, tokens: [...path], fees: [...poolFees] },
  };
  for (let index = 0; index < (callIndex === 3 ? 2 : callIndex); index++) {
    const approval = calls[index]!;
    if (!isAddressEqual(approval.to, tokenIn) || approval.value !== 0n) throw new Error('Invalid approval call');
    const parsed = decodeFunctionData({ abi: approvalAbi, data: approval.data });
    if (encodeFunctionData({ abi: approvalAbi, functionName: 'approve', args: parsed.args }).toLowerCase() !== approval.data.toLowerCase()) throw new Error('Noncanonical approval');
    decoded.commands.push({ kind: 'APPROVAL', callIndex: index, token: approval.to, spender: parsed.args[0], amount: parsed.args[1] });
  }
  if (callIndex === 3) {
    const approval = calls[2]!;
    if (approval.value !== 0n || !policy.permittedApprovalSpenders.some(spender => isAddressEqual(spender, approval.to))
      || isAddressEqual(approval.to, tokenIn) || isAddressEqual(approval.to, policy.router)) throw new Error('Invalid Permit2 approval target');
    const parsed = decodeFunctionData({ abi: permit2ApprovalAbi, data: approval.data });
    if (encodeFunctionData({ abi: permit2ApprovalAbi, functionName: 'approve', args: parsed.args }).toLowerCase() !== approval.data.toLowerCase()) throw new Error('Noncanonical Permit2 approval');
    decoded.commands.push({ kind: 'PERMIT2_APPROVAL', callIndex: 2, permit2: approval.to,
      token: parsed.args[0], spender: parsed.args[1], amount: parsed.args[2], expiration: BigInt(parsed.args[3]) });
  }
  decoded.commands.push({ kind: 'SWAP', callIndex, router: call.to, swapper: policy.vault,
    recipient: recipientIsSender ? policy.vault : sweep[1], tokenIn: path[0]!, tokenOut: path.at(-1)!,
    amountIn, minimumAmountOut: sweep[2], deadlineUnixSeconds: inspected.deadline,
    feeBps: Number(fee[2] / feeScale), feeRecipient: fee[1] });
  const validation = validatePreparedTrade({ intent, quote, policy, calls, decoded });
  if (!validation.valid) throw new Error(`Trade policy rejected: ${validation.failures.join(', ')}`);
  return decoded;
}
